'use client';

import { useTranslations } from 'next-intl';
import type { MouseEvent, ReactNode } from 'react';
import { useRef, useState } from 'react';

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/app-popover';
import { NetworkLink } from '@/i18n/network-link';
import { cn } from '@/lib/ui/utils';

import {
  copyMorfiVortexLogoPng,
  copyMorfiVortexLogoSvg,
  resolveMorfiVortexLogoFill,
} from './copy-morfi-vortex-logo';
import { MorfiVortexLogo } from './morfi-vortex-logo';

const copyMenuItemClassName: string = cn(
  'flex w-full cursor-default items-center justify-center rounded-item px-3 py-1.5 text-sm outline-hidden select-none',
  'hover:bg-background-tertiary focus-visible:bg-background-tertiary',
);

interface MorfiVortexLogoCopyMenuProps {
  size?: number;
}

export function MorfiVortexLogoCopyMenu({ size = 32 }: MorfiVortexLogoCopyMenuProps): ReactNode {
  const tHeader = useTranslations('supportMainnet.landingHeader');
  const t = useTranslations('supportMainnet.landingHeader.logoCopy');
  const logoRootRef = useRef<HTMLSpanElement>(null);
  const openFromContextMenuRef = useRef(false);
  const [open, setOpen] = useState(false);

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen && !openFromContextMenuRef.current) {
      return;
    }
    openFromContextMenuRef.current = false;
    setOpen(nextOpen);
  }

  function handleContextMenu(event: MouseEvent<HTMLSpanElement>): void {
    event.preventDefault();
    openFromContextMenuRef.current = true;
    setOpen(true);
  }

  function resolveFill(): string {
    return resolveMorfiVortexLogoFill(logoRootRef.current);
  }

  function handleCopySvg(): void {
    void copyMorfiVortexLogoSvg(resolveFill(), t('svgCopied'), t('copyFailed'));
    setOpen(false);
  }

  function handleCopyPng(): void {
    void copyMorfiVortexLogoPng(resolveFill(), t('pngCopied'), t('copyFailed'));
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor asChild>
        <span ref={logoRootRef} className="inline-flex">
          <NetworkLink
            href="/"
            className="flex shrink-0 items-center"
            aria-label={tHeader('navigation')}
            onContextMenu={handleContextMenu}
          >
            <MorfiVortexLogo size={size} />
          </NetworkLink>
        </span>
      </PopoverAnchor>
      <PopoverContent
        align="center"
        side="bottom"
        sideOffset={20}
        className="w-max min-w-0 p-1"
        onOpenAutoFocus={(event: Event): void => {
          event.preventDefault();
        }}
      >
        <div role="menu" className="flex flex-col items-stretch">
          <button
            type="button"
            role="menuitem"
            className={copyMenuItemClassName}
            onClick={handleCopySvg}
          >
            {t('copySvg')}
          </button>
          <button
            type="button"
            role="menuitem"
            className={copyMenuItemClassName}
            onClick={handleCopyPng}
          >
            {t('copyPng')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
